import { SchemaIO, SchemaTemplatorV2 } from '@frontend/schema';
import { useMutation } from 'react-query';
import { MutationKeysMap } from './types';
import { useDefaultLocationIdHeaderOptions, useHandleTemplateResponse } from './query-utils';
import { LimitedSchemaMutationOptions } from '@frontend/react-query-helpers';

export const mutationKeys: MutationKeysMap = {
  baseKey: () => ['templator-v2'],
  createTemplate: () => [...mutationKeys.baseKey(), 'createTemplate'],
  updateTemplate: () => [...mutationKeys.baseKey(), 'updateTemplate'],
  deleteTemplate: () => [...mutationKeys.baseKey(), 'deleteTemplate'],
};

type CreateTemplateIO = SchemaIO<(typeof SchemaTemplatorV2)['TemplateCreate']>;
export const useCreateTemplateMutation = <E = unknown, C = unknown>(
  options?: LimitedSchemaMutationOptions<CreateTemplateIO, E, C>
) => {
  const mutationKey = mutationKeys.createTemplate();
  const { getHeaderOptions } = useDefaultLocationIdHeaderOptions();
  const handleTemplateResponse = useHandleTemplateResponse();
  const mutation = useMutation({
    mutationKey,
    mutationFn: (req) => SchemaTemplatorV2.TemplateCreate(req, getHeaderOptions(req.businessGroupIds)),
    ...options,
    onSuccess: (res, ...rest) => {
      options?.onSuccess?.(res, ...rest);
      handleTemplateResponse({ payload: res.template, action: 'create' });
    },
  });
  return {
    ...mutation,
    mutationKey,
  };
};

type UpdateTemplateIO = SchemaIO<(typeof SchemaTemplatorV2)['TemplateUpdate']>;
export const useUpdateTemplateMutation = <E = unknown, C = unknown>(
  options?: LimitedSchemaMutationOptions<UpdateTemplateIO, E, C>
) => {
  const mutationKey = mutationKeys.updateTemplate();
  const { getHeaderOptions } = useDefaultLocationIdHeaderOptions();
  const handleTemplateResponse = useHandleTemplateResponse();
  const mutation = useMutation({
    mutationKey,
    mutationFn: (req) => SchemaTemplatorV2.TemplateUpdate(req, getHeaderOptions(req.businessGroupIds)),
    ...options,
    onSuccess: (res, ...rest) => {
      options?.onSuccess?.(res, ...rest);
      handleTemplateResponse({ payload: res.template, action: 'update' });
    },
  });
  return {
    ...mutation,
    mutationKey,
  };
};

type DeleteTemplateIO = SchemaIO<(typeof SchemaTemplatorV2)['TemplateDelete']>;
export const useDeleteTemplateMutation = <E = unknown, C = unknown>(
  options?: LimitedSchemaMutationOptions<DeleteTemplateIO, E, C>
) => {
  const mutationKey = mutationKeys.deleteTemplate();
  const { getHeaderOptions } = useDefaultLocationIdHeaderOptions();
  const handleTemplateResponse = useHandleTemplateResponse();
  const mutation = useMutation({
    mutationKey,
    mutationFn: (req) => SchemaTemplatorV2.TemplateDelete(req, getHeaderOptions()),
    ...options,
    onSuccess: (res, req, ...rest) => {
      options?.onSuccess?.(res, req, ...rest);
      handleTemplateResponse({ payload: req, action: 'delete' });
    },
  });
  return {
    ...mutation,
    mutationKey,
  };
};
